import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplateCategories } from '../../redux/slices/templateCategorySlice';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';

/**
 * BasicInfoTab - Basic template information tab
 * 
 * Fields:
 * - Title (required)
 * - Description
 * - Category (dropdown)
 * - Is Public (switch) - Only visible for system admins
 * - Credit Cost (number) - Only visible for system admins with templates.manage_credits permission
 */
const BasicInfoTab = ({ template, onChange, showIsPublic = false, showCreditCost = false, creditPriceTL = 10, defaultTemplateCredits = 1 }) => {
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.templateCategories.list);

  // Her mount'ta kategorileri fetch et (güncel liste için)
  useEffect(() => {
    dispatch(fetchTemplateCategories());
  }, [dispatch]);

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography variant="h6" gutterBottom>
        Temel Bilgiler
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Şablonun temel bilgilerini girin. Başlık zorunludur.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Title */}
        <TextField
          fullWidth
          label="Şablon Başlığı"
          value={template.title || ''}
          onChange={(e) => onChange('title', e.target.value)}
          required
          placeholder="Örn: Kira Sözleşmesi"
          helperText="Şablonun görünen adı"
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Açıklama"
          value={template.description || ''}
          onChange={(e) => onChange('description', e.target.value)}
          multiline
          rows={4}
          placeholder="Şablon hakkında kısa bir açıklama..."
          helperText="Şablonun ne için kullanıldığını açıklayın"
        />

        {/* Category */}
        <FormControl fullWidth>
          <InputLabel>Kategori</InputLabel>
          <Select
            value={template.category || 'Diğer'}
            onChange={(e) => onChange('category', e.target.value)}
            label="Kategori"
          >
            {categories.length > 0 ? (
              categories.map((cat) => (
                <MenuItem key={cat._id} value={cat.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: cat.color || '#607D8B',
                        flexShrink: 0,
                      }}
                    />
                    {cat.name}
                  </Box>
                </MenuItem>
              ))
            ) : (
              // Kategoriler yüklenmediyse mevcut değeri koru
              <MenuItem value={template.category || 'Diğer'}>
                {template.category || 'Diğer'}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Is Public - Only visible for system admins */}
        {showIsPublic && (
          <FormControlLabel
            control={
              <Switch
                checked={template.isPublic !== undefined ? template.isPublic : true}
                onChange={(e) => onChange('isPublic', e.target.checked)}
              />
            }
            label="Genel Şablon (Herkes kullanabilir)"
          />
        )}

        {/* Credit Cost - Only visible for system admins with templates.manage_credits permission */}
        {showCreditCost && (
          <TextField
            fullWidth
            label="Kredi Maliyeti"
            type="number"
            value={template.creditCost !== null && template.creditCost !== undefined ? template.creditCost : defaultTemplateCredits}
            onChange={(e) => {
              const value = e.target.value === '' ? null : parseFloat(e.target.value);
              onChange('creditCost', value);
            }}
            inputProps={{ min: 0, step: 'any' }}
            helperText={
              (() => {
                const credits = template.creditCost !== null && template.creditCost !== undefined 
                  ? parseFloat(template.creditCost) 
                  : defaultTemplateCredits;
                const tlEquivalent = credits * creditPriceTL;
                const isDefault = template.creditCost === null || template.creditCost === undefined;
                return `TL Karşılığı: ${tlEquivalent.toFixed(2)} TL${isDefault ? ' (default_template_credits)' : ''}`;
              })()
            }
            placeholder="Boş bırakılırsa default_template_credits kullanılır"
          />
        )}
      </Box>
    </Box>
  );
};

BasicInfoTab.propTypes = {
  template: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.string,
    isPublic: PropTypes.bool,
    creditCost: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  showIsPublic: PropTypes.bool,
  showCreditCost: PropTypes.bool,
  creditPriceTL: PropTypes.number,
  defaultTemplateCredits: PropTypes.number,
};

export default BasicInfoTab;

